/**
 * ActionButtons - Mobile touch buttons for character actions
 *
 * Displays action buttons (jump, attack, etc.) in the bottom-right corner
 * for touch devices. Layout inspired by mobile game controls.
 */
export default class ActionButtons {
  /**
   * Create action buttons
   * @param {Object} options - Configuration options
   */
  constructor(options = {}) {
    // Configuration
    this.buttonSize = options.buttonSize || 56;       // Button size in pixels (all same size)
    this.padding = options.padding || 20;             // Padding from screen edge
    this.spacing = options.spacing || 8;              // Space between buttons

    // Callbacks for button actions
    this.onJump = options.onJump || null;
    this.onAttack = options.onAttack || null;
    this.onAction1 = options.onAction1 || null;
    this.onAction2 = options.onAction2 || null;

    // Track active touches per button
    this.activeTouches = new Map();

    // Create DOM elements
    this.createElements();

    // Bind event handlers
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    // Only enable on touch devices
    if (this.isTouchDevice()) {
      this.enable();
    }
  }

  /**
   * Check if device supports touch
   * @returns {boolean}
   */
  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Create the button DOM elements
   */
  createElements() {
    // Container for all action buttons
    this.container = document.createElement('div');
    this.container.className = 'action-buttons-container';

    // All buttons same size, arranged in 2x2 square grid
    // [X] [Y]
    // [B] [A]
    this.jumpButton = this.createButton('jump', 'A', this.buttonSize);
    this.jumpButton.classList.add('action-btn-jump');

    this.attackButton = this.createButton('attack', 'B', this.buttonSize);
    this.attackButton.classList.add('action-btn-attack');

    this.action1Button = this.createButton('action1', 'X', this.buttonSize);
    this.action1Button.classList.add('action-btn-action1');

    this.action2Button = this.createButton('action2', 'Y', this.buttonSize);
    this.action2Button.classList.add('action-btn-action2');

    // Position buttons in 2x2 square grid
    this.positionButtons();

    // Add to container
    this.container.appendChild(this.jumpButton);
    this.container.appendChild(this.attackButton);
    this.container.appendChild(this.action1Button);
    this.container.appendChild(this.action2Button);

    // Add to game container (same stacking context as windows)
    const gameContainer = document.getElementById('container') || document.body;
    gameContainer.appendChild(this.container);
  }

  /**
   * Create a single button element
   * @param {string} action - Action name
   * @param {string} label - Button label
   * @param {number} size - Button size in pixels
   * @returns {HTMLElement}
   */
  createButton(action, label, size) {
    const button = document.createElement('div');
    button.className = 'action-button';
    button.dataset.action = action;
    button.style.width = `${size}px`;
    button.style.height = `${size}px`;

    // Label
    const labelEl = document.createElement('span');
    labelEl.className = 'action-button-label';
    labelEl.textContent = label;
    button.appendChild(labelEl);

    return button;
  }

  /**
   * Position buttons in a 2x2 square grid layout
   * [X] [Y]
   * [B] [A]
   */
  positionButtons() {
    const p = this.padding;
    const s = this.spacing;
    const size = this.buttonSize;

    // Container positioning
    this.container.style.position = 'fixed';
    this.container.style.right = `${p}px`;
    this.container.style.bottom = `${p}px`;
    this.container.style.pointerEvents = 'none';
    this.container.style.zIndex = '9997';

    // 2x2 grid positions (all relative to container at bottom-right)
    // A (jump) - bottom right
    this.jumpButton.style.position = 'absolute';
    this.jumpButton.style.right = '0';
    this.jumpButton.style.bottom = '0';

    // B (attack) - bottom left
    this.attackButton.style.position = 'absolute';
    this.attackButton.style.right = `${size + s}px`;
    this.attackButton.style.bottom = '0';

    // Y (action2) - top right
    this.action2Button.style.position = 'absolute';
    this.action2Button.style.right = '0';
    this.action2Button.style.bottom = `${size + s}px`;

    // X (action1) - top left
    this.action1Button.style.position = 'absolute';
    this.action1Button.style.right = `${size + s}px`;
    this.action1Button.style.bottom = `${size + s}px`;
  }

  /**
   * Enable button event listeners
   */
  enable() {
    this.container.style.display = 'block';

    // Add touch listeners to each button
    const buttons = [this.jumpButton, this.attackButton, this.action1Button, this.action2Button];
    buttons.forEach(btn => {
      btn.style.pointerEvents = 'auto';
      btn.addEventListener('touchstart', this.onTouchStart, { passive: false });
      btn.addEventListener('touchend', this.onTouchEnd, { passive: false });
      btn.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
    });
  }

  /**
   * Disable button event listeners
   */
  disable() {
    this.container.style.display = 'none';

    const buttons = [this.jumpButton, this.attackButton, this.action1Button, this.action2Button];
    buttons.forEach(btn => {
      btn.removeEventListener('touchstart', this.onTouchStart);
      btn.removeEventListener('touchend', this.onTouchEnd);
      btn.removeEventListener('touchcancel', this.onTouchEnd);
    });
  }

  /**
   * Handle touch start on buttons
   * @param {TouchEvent} e
   */
  onTouchStart(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.currentTarget;
    const action = button.dataset.action;
    const touch = e.changedTouches[0];

    // Track this touch
    this.activeTouches.set(touch.identifier, action);

    // Visual feedback
    button.classList.add('active');

    // Trigger callback
    this.triggerAction(action);
  }

  /**
   * Handle touch end on buttons
   * @param {TouchEvent} e
   */
  onTouchEnd(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.currentTarget;

    // Remove visual feedback
    button.classList.remove('active');

    // Clean up tracked touches
    for (const touch of e.changedTouches) {
      this.activeTouches.delete(touch.identifier);
    }
  }

  /**
   * Trigger the appropriate callback for an action
   * @param {string} action
   */
  triggerAction(action) {
    switch (action) {
      case 'jump':
        if (this.onJump) this.onJump();
        break;
      case 'attack':
        if (this.onAttack) this.onAttack();
        break;
      case 'action1':
        if (this.onAction1) this.onAction1();
        break;
      case 'action2':
        if (this.onAction2) this.onAction2();
        break;
    }
  }

  /**
   * Set callback for jump button
   * @param {Function} callback
   */
  setJumpCallback(callback) {
    this.onJump = callback;
  }

  /**
   * Set callback for attack button
   * @param {Function} callback
   */
  setAttackCallback(callback) {
    this.onAttack = callback;
  }

  /**
   * Set callback for action1 button
   * @param {Function} callback
   */
  setAction1Callback(callback) {
    this.onAction1 = callback;
  }

  /**
   * Set callback for action2 button
   * @param {Function} callback
   */
  setAction2Callback(callback) {
    this.onAction2 = callback;
  }

  /**
   * Destroy and clean up
   */
  destroy() {
    this.disable();
    this.container.remove();
  }
}
